/*
 * Copyright 2026 Sony Corporation
 */
import type { Guideline } from '@ips-jit/schema';

/** Tokens as people read them: 950, 12.3k, 4.5M, 1.2B. */
export const formatTokens = (n: number): string => {
  const abs = Math.abs(n);
  const short = (value: number, unit: string) =>
    `${value >= 100 ? Math.round(value) : Number(value.toFixed(1))}${unit}`;
  if (abs >= 1e9) {
    return short(n / 1e9, 'B');
  }
  if (abs >= 1e6) {
    return short(n / 1e6, 'M');
  }
  if (abs >= 1e3) {
    return short(n / 1e3, 'k');
  }
  return String(Math.round(n));
};

export const formatUsd = (usd: number): string =>
  usd >= 100 ? `$${Math.round(usd).toLocaleString('en-US')}` : `$${usd.toFixed(2)}`;

export const formatPercent = (percent: number): string =>
  `${percent >= 10 || percent === 0 ? Math.round(percent) : percent.toFixed(1)}%`;

/** About what a number of tokens costs, at the guideline's rate. */
export const tokensToUsd = (tokens: number, guideline: Guideline): number =>
  (tokens / 1_000_000) * guideline.usd_per_mtoken;

/** About how many apps a number of tokens makes, at the guideline's rate; whole apps, rounded down. */
export const tokensToApps = (tokens: number, guideline: Guideline): number =>
  Math.max(0, Math.floor(tokens / guideline.tokens_per_app));

/** The day a month's count starts over, as in "1 Dec". */
export const formatResetDay = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });

/** "2026-11" as "November 2026". */
export const formatPeriod = (period: string): string => {
  const [year, month] = period.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString('en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

/** This month and the `count - 1` before it, newest first, as "YYYY-MM". */
export const recentPeriods = (count: number, now: Date = new Date()): string[] =>
  Array.from({ length: count }, (_, i) =>
    new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1)).toISOString().slice(0, 7)
  );
